import { type FC, type FormEvent, useEffect, useRef, useState } from "react";
import {
  ControlBar,
  RoomAudioRenderer,
  VideoTrack,
  useChat,
  useLocalParticipant,
  useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { armRoomIdleWatch, noteRoomMediaActivity } from "../Meeting/roomEngagement";

/** Compat React 19 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ControlBarView = ControlBar as unknown as FC<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VideoTrackView = VideoTrack as unknown as FC<any>;

function trackKey(trackRef: { publication?: { trackSid?: string }; participant?: { identity?: string }; source?: Track.Source }) {
  const sid = trackRef.publication?.trackSid;
  if (sid) {
    return sid;
  }
  return `${trackRef.participant?.identity || "p"}-${trackRef.source || "cam"}`;
}

function SquareVideoGrid({ raisedHandUids }: { raisedHandUids: ReadonlySet<string> }) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const tileCount = Math.max(tracks.length, 1);
  const columns = tileCount <= 1 ? 1 : tileCount <= 4 ? 2 : 3;

  return (
    <div
      className="grid h-full w-full gap-0.5 p-0.5"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {tracks.length === 0 ? (
        <div className="flex aspect-square items-center justify-center rounded-sm bg-gray-800 text-center text-xs text-gray-400">
          Ative a câmera nos controles abaixo
        </div>
      ) : (
        tracks.map((trackRef) => (
          <div
            key={trackKey(trackRef)}
            className="relative aspect-square overflow-hidden rounded-sm bg-gray-800"
          >
            <VideoTrackView
              trackRef={trackRef}
              className="absolute inset-0 h-full w-full object-cover"
            />
            {raisedHandUids.has(trackRef.participant?.identity || "") ? (
              <span
                className="absolute left-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-400 text-base shadow"
                aria-label="Pediu para falar"
              >
                ✋
              </span>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}

function LiveKitChatPanel() {
  const { chatMessages, send, isSending } = useChat();
  const [message, setMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [chatMessages.length]);

  const handleSend = async (event?: FormEvent) => {
    event?.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isSending) {
      return;
    }
    await send(trimmed);
    setMessage("");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-gray-700/50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Mensagens
      </div>
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2 text-sm"
      >
        {chatMessages.length === 0 ? (
          <p className="text-xs italic text-gray-500">Nenhuma mensagem ainda.</p>
        ) : (
          chatMessages.map((msg, index) => (
            <div key={`${msg.timestamp}-${index}`} className="leading-snug text-gray-200">
              <span className="font-semibold text-blue-300">
                {msg.from?.name || msg.from?.identity || "Participante"}:
              </span>{" "}
              {msg.message}
            </div>
          ))
        )}
      </div>
      <form
        onSubmit={handleSend}
        className="flex flex-shrink-0 items-center gap-2 border-t border-gray-700/50 p-2"
      >
        <input
          type="text"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Digite uma mensagem…"
          className="min-w-0 flex-1 rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-blue-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={isSending || !message.trim()}
          className="flex-shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Enviar
        </button>
      </form>
    </div>
  );
}

function LocalMediaBootstrap() {
  const { localParticipant } = useLocalParticipant();

  useEffect(() => {
    if (!localParticipant) {
      return;
    }
    void localParticipant.setCameraEnabled(true);
    void localParticipant.setMicrophoneEnabled(true);
  }, [localParticipant]);

  return null;
}

function LiveKitIdleSignals({ roomId }: { roomId: string }) {
  const { localParticipant } = useLocalParticipant();
  const micOn = localParticipant?.isMicrophoneEnabled === true;
  const cameraOn = localParticipant?.isCameraEnabled === true;

  useEffect(() => {
    void armRoomIdleWatch(roomId);
  }, [roomId]);

  useEffect(() => {
    const touch = () => {
      if (micOn) void noteRoomMediaActivity(roomId, "mic");
      if (cameraOn) void noteRoomMediaActivity(roomId, "camera");
    };
    touch();
    const intervalId = window.setInterval(touch, 20_000);
    return () => window.clearInterval(intervalId);
  }, [roomId, micOn, cameraOn]);

  return null;
}

export default function LiveKitVideoPanel({
  roomId,
  raisedHandUids = [],
}: {
  roomId: string;
  raisedHandUids?: readonly string[];
}) {
  const raised = new Set(raisedHandUids);
  return (
    <>
      <RoomAudioRenderer />
      <LocalMediaBootstrap />
      <LiveKitIdleSignals roomId={roomId} />
      <div className="lk-video-panel flex h-full min-h-0 flex-col bg-gray-900">
        <div className="lk-video-panel__grid w-full flex-shrink-0 aspect-square max-h-[min(20rem,42dvh)] overflow-hidden border-b border-gray-700/50">
          <SquareVideoGrid raisedHandUids={raised} />
        </div>

        <div className="lk-video-panel__chat flex min-h-0 flex-1 flex-col overflow-hidden">
          <LiveKitChatPanel />
        </div>

        <div className="lk-video-panel__controls flex-shrink-0 border-t border-gray-700/50 bg-gray-900/95">
          <ControlBarView
            variation="minimal"
            controls={{
              chat: false,
              screenShare: true,
              leave: false,
            }}
          />
        </div>
      </div>
    </>
  );
}
